import React, { useState, useEffect } from 'react';
import { Download, Play, Users, TrendingUp, Zap, Globe, Code, Smartphone, Database, BarChart3 } from 'lucide-react';

const KapilTechnologiesShowcase = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className={`max-w-7xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
        
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            Latest insights and events
          </h1>
        </div>

        {/* Main Grid Layout */}
        <div className="grid grid-cols-12 gap-6 mb-12">
          
          {/* Large Report Card - Top Left */}
          <div 
            className="col-span-12 lg:col-span-6 relative overflow-hidden rounded-2xl shadow-lg transform transition-all duration-500 hover:scale-105 hover:shadow-xl cursor-pointer"
            style={{ backgroundColor: '#4ECDC4', minHeight: '400px' }}
            onMouseEnter={() => setHoveredCard(1)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-10">
              <div className="absolute top-0 right-0 w-48 h-48 rounded-full border-4 border-white transform translate-x-12 -translate-y-12"></div>
              <div className="absolute bottom-0 left-0 w-32 h-32 rounded-full border-2 border-white transform -translate-x-8 translate-y-8"></div>
            </div>

            <div className="relative p-8 h-full flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center space-x-2 bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold mb-6">
                  <BarChart3 className="w-5 h-5" style={{ color: '#4ECDC4' }} />
                  <span>Report</span>
                </div>
                <h2 className="text-white text-3xl font-bold mb-6 leading-tight">
                  Explore the latest Digital Transformation Report: Our solutions in action
                </h2>
              </div>
              
              <div className={`flex items-center text-white font-semibold text-lg transition-all duration-300 ${
                hoveredCard === 1 ? 'translate-x-2' : ''
              }`}>
                <Download className="w-6 h-6 mr-3" style={{ color: '#FFD93D' }} />
                <span>Download the report</span>
              </div>
            </div>
          </div>

          {/* Article Card - Top Right */}
          <div 
            className="col-span-12 lg:col-span-6 relative overflow-hidden rounded-2xl shadow-lg transform transition-all duration-500 hover:scale-105 hover:shadow-xl cursor-pointer"
            style={{ backgroundColor: '#E85A8B', minHeight: '400px' }}
            onMouseEnter={() => setHoveredCard(2)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="relative p-8 h-full flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center space-x-2 bg-black text-white px-4 py-2 rounded-lg text-sm font-semibold mb-6">
                  <Code className="w-5 h-5" style={{ color: '#E85A8B' }} />
                  <span>Article</span>
                </div>
                <h2 className="text-white text-3xl font-bold mb-6 leading-tight">
                  Innovation in ERP Solutions
                </h2>
                <p className="text-white text-opacity-90 mb-6 text-lg leading-relaxed">
                  Read this article on comprehensive ERP implementations and advanced technology solutions
                </p>
              </div>
            </div>
          </div>

          {/* Image Card with Person - Bottom Left */}
          <div className="col-span-12 lg:col-span-6 relative overflow-hidden rounded-2xl shadow-lg transform transition-all duration-500 hover:scale-105 hover:shadow-xl">
            <div className="relative h-80 bg-gradient-to-br from-gray-200 to-gray-300">
              {/* Simulated person image with colorful background */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-400 to-purple-500 opacity-20"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="inline-flex items-center space-x-2 text-white px-4 py-2 rounded-lg text-sm font-semibold mb-4" style={{ backgroundColor: '#E85A8B' }}>
                  <Users className="w-5 h-5" style={{ color: '#FFD93D' }} />
                  <span>Our Impact</span>
                </div>
                <h3 className="text-white text-xl font-bold">
                  We help clients digitally transform to better serve their customers
                </h3>
              </div>
              {/* Abstract person silhouette */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-white opacity-30"></div>
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/4 w-16 h-16 rounded-full bg-white opacity-50"></div>
            </div>
          </div>

          {/* Event Card - Bottom Center */}
          <div 
            className="col-span-12 lg:col-span-3 relative overflow-hidden rounded-2xl shadow-lg transform transition-all duration-500 hover:scale-105 hover:shadow-xl cursor-pointer"
            style={{ backgroundColor: '#FF9F43', minHeight: '300px' }}
            onMouseEnter={() => setHoveredCard(3)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            <div className="relative p-6 h-full flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center space-x-2 bg-black text-white px-3 py-2 rounded-lg text-sm font-semibold mb-4">
                  <Zap className="w-4 h-4" style={{ color: '#FF9F43' }} />
                  <span>Event</span>
                </div>
                <h3 className="text-white text-xl font-bold mb-4 leading-tight">
                  Your path to digital and ERP leadership
                </h3>
              </div>
            </div>
          </div>

          {/* Webinar Card - Bottom Right */}
          <div 
            className="col-span-12 lg:col-span-3 relative overflow-hidden rounded-2xl shadow-lg transform transition-all duration-500 hover:scale-105 hover:shadow-xl cursor-pointer"
            style={{ backgroundColor: '#7ED321', minHeight: '300px' }}
            onMouseEnter={() => setHoveredCard(4)}
            onMouseLeave={() => setHoveredCard(null)}
          >
            {/* Circular pattern background */}
            <div className="absolute inset-0 opacity-20">
              {[...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="absolute rounded-full border-2 border-white"
                  style={{
                    width: `${(i + 1) * 30}px`,
                    height: `${(i + 1) * 30}px`,
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                  }}
                ></div>
              ))}
            </div>

            <div className="relative p-6 h-full flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center space-x-2 bg-black text-white px-3 py-2 rounded-lg text-sm font-semibold mb-4">
                  <Globe className="w-4 h-4" style={{ color: '#7ED321' }} />
                  <span>Webinar</span>
                </div>
                <h3 className="text-white text-xl font-bold mb-4 leading-tight">
                  Technology Solutions Vol.32 webinar
                </h3>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Impact Section */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl p-8 text-center text-white">
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">Our Impact</h2>
            <p className="text-lg opacity-90 max-w-4xl mx-auto">
              We help clients digitally transform to better serve their customers. Here's how we deliver cutting-edge ERP implementations, 
              advanced web and mobile applications, and innovative marketing solutions across India, USA, Indonesia, Malaysia, and Singapore.
            </p>
          </div>
          
          {/* Impact Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[
              { 
                icon: <Database className="w-8 h-8" />, 
                title: 'ERP Solutions', 
                desc: 'Comprehensive implementations',
                color: '#4ECDC4'
              },
              { 
                icon: <Smartphone className="w-8 h-8" />, 
                title: 'Mobile Apps', 
                desc: 'Advanced development',
                color: '#E85A8B'
              },
              { 
                icon: <Code className="w-8 h-8" />, 
                title: 'Web Solutions', 
                desc: 'Scalable applications',
                color: '#FF9F43'
              },
              { 
                icon: <TrendingUp className="w-8 h-8" />, 
                title: 'Global Reach', 
                desc: '5 countries served',
                color: '#7ED321'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white bg-opacity-10 rounded-2xl p-6 backdrop-blur-sm">
                <div className="flex justify-center mb-4" style={{ color: item.color }}>
                  {item.icon}
                </div>
                <h3 className="font-bold text-lg mb-2 text-center">{item.title}</h3>
                <p className="text-sm opacity-80 text-center">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KapilTechnologiesShowcase;